import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose, DialogDescription } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Plus, MagnifyingGlass, Calendar as CalendarIcon, Funnel, X,
  PencilSimple, Trash, Check, Upload, FileArrowDown,
  CaretDown, Warning, CheckCircle, CurrencyDollar, ArrowCounterClockwise, Eye, EyeSlash
} from "@phosphor-icons/react";

export default function BillsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [bills, setBills] = useState([]);
  const [categories, setCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  
  // Status filter from URL (paid, pending, overdue, all)
  const [statusFilter, setStatusFilter] = useState(searchParams.get('filter') || "all");
  const highlightBillId = searchParams.get('highlight');
  
  // Alert dialogs
  const [payAlertOpen, setPayAlertOpen] = useState(false);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [pendingPayment, setPendingPayment] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [selectedPayDate, setSelectedPayDate] = useState(format(new Date(), "yyyy-MM-dd"));
  
  // Paid installments modal
  const [paidModalOpen, setPaidModalOpen] = useState(false);
  const [showTotalPending, setShowTotalPending] = useState(true);
  const [unpayAlertOpen, setUnpayAlertOpen] = useState(false);
  const [pendingUnpay, setPendingUnpay] = useState(null);
  
  // Edit installments modal
  const [installmentsModalOpen, setInstallmentsModalOpen] = useState(false);
  const [selectedBillForInstallments, setSelectedBillForInstallments] = useState(null);
  const [editingInstallmentDates, setEditingInstallmentDates] = useState({});

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    description: "",
    total_value: "",
    installments: 1,
    start_date: format(new Date(), "yyyy-MM-dd"),
    category_id: "",
    notes: "",
    is_fixed: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-scroll to highlighted bill from reports
  useEffect(() => {
    if (highlightBillId && !loading) {
      setTimeout(() => {
        const el = document.querySelector(`[data-bill-id="${highlightBillId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-primary", "ring-offset-2");
          setTimeout(() => el.classList.remove("ring-2", "ring-primary", "ring-offset-2"), 3000);
        }
      }, 500);
    }
  }, [highlightBillId, loading]);

  const fetchData = async () => {
    try {
      const [billsRes, catsRes, pmRes] = await Promise.all([
        api.get("/bills"),
        api.get("/categories"),
        api.get("/payment-methods")
      ]);
      setBills(billsRes.data);
      setCategories(catsRes.data);
      setPaymentMethods(pmRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const fetchBillsWithFilters = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterMonth) params.append("month", filterMonth);
      if (filterYear) params.append("year", filterYear);
      if (filterCategory) params.append("category_id", filterCategory);
      if (filterStartDate) params.append("start_date", filterStartDate);
      if (filterEndDate) params.append("end_date", filterEndDate);
      
      const response = await api.get(`/bills?${params.toString()}`);
      setBills(response.data);
    } catch (error) {
      toast.error("Erro ao filtrar contas");
    }
  }, [filterMonth, filterYear, filterCategory, filterStartDate, filterEndDate]);

  useEffect(() => {
    if (filterMonth || filterYear || filterCategory || filterStartDate || filterEndDate) {
      fetchBillsWithFilters();
    }
  }, [fetchBillsWithFilters, filterMonth, filterYear, filterCategory, filterStartDate, filterEndDate]);

  const resetForm = () => {
    setFormData({
      description: "",
      total_value: "",
      installments: 1,
      start_date: format(new Date(), "yyyy-MM-dd"),
      category_id: "",
      notes: "",
      is_fixed: false
    });
    setEditingBill(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        total_value: parseFloat(formData.total_value),
        installments: parseInt(formData.installments),
        category_id: formData.category_id || null
      };

      if (editingBill) {
        await api.put(`/bills/${editingBill.id}`, payload);
        toast.success("Conta atualizada!");
      } else {
        await api.post("/bills", payload);
        toast.success("Conta cadastrada!");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar conta");
    }
  };

  // Show delete confirmation dialog
  const confirmDelete = (bill) => {
    setPendingDelete(bill);
    setDeleteAlertOpen(true);
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/bills/${pendingDelete.id}`);
      toast.success("Conta movida para lixeira");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir conta");
    } finally {
      setDeleteAlertOpen(false);
      setPendingDelete(null);
    }
  };

  // Show pay confirmation dialog
  const confirmPayInstallment = (bill, installment) => {
    setPendingPayment({ bill, installment });
    setSelectedPaymentMethod("");
    setPayAlertOpen(true);
  };

  const handlePayInstallment = async () => {
    if (!pendingPayment) return;
    const { bill, installment } = pendingPayment;
    try {
      await api.post(`/bills/${bill.id}/pay`, { 
        bill_id: bill.id, 
        installment_number: installment.installment_number,
        payment_method_id: selectedPaymentMethod || null,
        paid_at: selectedPayDate ? new Date(selectedPayDate + "T12:00:00").toISOString() : null
      });
      toast.success("Parcela marcada como paga!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao marcar parcela");
    } finally {
      setPayAlertOpen(false);
      setPendingPayment(null);
      setSelectedPaymentMethod("");
      setSelectedPayDate(format(new Date(), "yyyy-MM-dd"));
    }
  };

  // Confirm unpay (revert payment)
  const confirmUnpayInstallment = (bill, installment) => {
    setPendingUnpay({ bill, installment });
    setUnpayAlertOpen(true);
  };

  const handleUnpayInstallment = async () => {
    if (!pendingUnpay) return;
    const { bill, installment } = pendingUnpay;
    try {
      await api.post(`/bills/${bill.id}/unpay`, { 
        bill_id: bill.id, 
        installment_number: installment.installment_number 
      });
      toast.success(`Parcela ${installment.installment_number} revertida para pendente!`);
      fetchData();
    } catch (error) {
      toast.error("Erro ao reverter parcela");
    } finally {
      setUnpayAlertOpen(false);
      setPendingUnpay(null);
    }
  };

  const handleEdit = (bill) => {
    setEditingBill(bill);
    setFormData({
      description: bill.description,
      total_value: bill.total_value.toString(),
      installments: bill.installments,
      start_date: bill.start_date,
      category_id: bill.category_id || "",
      notes: bill.notes || "",
      is_fixed: bill.is_fixed
    });
    setDialogOpen(true);
  };

  // Open installments modal to edit due dates
  const openInstallmentsModal = (bill) => {
    setSelectedBillForInstallments(bill);
    // Initialize editing dates from current installment dates
    const dates = {};
    bill.installment_details?.forEach(inst => {
      dates[inst.installment_number] = inst.due_date;
    });
    setEditingInstallmentDates(dates);
    setInstallmentsModalOpen(true);
  };

  // Save updated installment due date
  const handleUpdateInstallmentDate = async (billId, installmentNumber, newDate) => {
    try {
      await api.put(`/bills/${billId}/installment`, {
        installment_number: installmentNumber,
        due_date: newDate
      });
      toast.success(`Data da parcela ${installmentNumber} atualizada!`);
      fetchData();
      
      // Update local state
      setEditingInstallmentDates(prev => ({
        ...prev,
        [installmentNumber]: newDate
      }));
      
      // Update selectedBillForInstallments
      if (selectedBillForInstallments) {
        const updatedBill = {
          ...selectedBillForInstallments,
          installment_details: selectedBillForInstallments.installment_details.map(inst =>
            inst.installment_number === installmentNumber
              ? { ...inst, due_date: newDate }
              : inst
          )
        };
        setSelectedBillForInstallments(updatedBill);
      }
    } catch (error) {
      toast.error("Erro ao atualizar data da parcela");
    }
  };

  const handleExport = async (type) => {
    try {
      const params = new URLSearchParams();
      if (filterMonth) params.append("month", filterMonth);
      if (filterYear) params.append("year", filterYear);
      if (filterCategory) params.append("category_id", filterCategory);

      const response = await api.get(`/export/${type}?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contas_a_pagar.${type === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Exportado como ${type.toUpperCase()}`);
    } catch (error) {
      toast.error("Erro ao exportar");
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterMonth("");
    setFilterYear("");
    setFilterCategory("");
    setFilterStartDate("");
    setFilterEndDate("");
    fetchData();
  };

  // Helper to check if a bill has installments matching the status filter
  const billMatchesStatusFilter = (bill, status) => {
    if (status === "all") return true;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return bill.installment_details?.some(inst => {
      const dueDate = new Date(inst.due_date);
      dueDate.setHours(0, 0, 0, 0);
      
      if (status === "paid") return inst.is_paid;
      if (status === "pending") return !inst.is_paid && dueDate >= today;
      if (status === "overdue") return !inst.is_paid && dueDate < today;
      return true;
    });
  };

  const filteredBills = bills.filter(bill => {
    // First, filter by search term
    if (!bill.description.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    // Then, filter by status if set
    if (statusFilter !== "all") {
      return billMatchesStatusFilter(bill, statusFilter);
    }
    return true;
  });

  // Calculate totals from filtered bills - considering date filters
  const calculateTotals = () => {
    let totalPending = 0;
    let totalPaid = 0;

    // Parse filter dates if set
    const startDt = filterStartDate ? new Date(filterStartDate) : null;
    const endDt = filterEndDate ? new Date(filterEndDate) : null;

    filteredBills.forEach(bill => {
      bill.installment_details?.forEach(inst => {
        const dueDate = new Date(inst.due_date);
        
        // Check if installment is within date range (if filters are set)
        let inDateRange = true;
        if (startDt && dueDate < startDt) inDateRange = false;
        if (endDt && dueDate > endDt) inDateRange = false;
        
        // Also check month/year filters
        if (filterMonth && (dueDate.getMonth() + 1) !== parseInt(filterMonth)) inDateRange = false;
        if (filterYear && dueDate.getFullYear() !== parseInt(filterYear)) inDateRange = false;

        // Only count installments within the date range
        if (inDateRange) {
          if (inst.is_paid) {
            totalPaid += inst.value;
          } else {
            totalPending += inst.value;
          }
        }
      });
    });

    return { totalPending, totalPaid };
  };

  const { totalPending, totalPaid } = calculateTotals();

  // Get all paid installments for the modal - respecting date filters
  const getPaidInstallments = () => {
    const paidInstallments = [];
    
    // Parse filter dates if set
    const startDt = filterStartDate ? new Date(filterStartDate) : null;
    const endDt = filterEndDate ? new Date(filterEndDate) : null;
    
    filteredBills.forEach(bill => {
      bill.installment_details?.forEach(inst => {
        if (inst.is_paid) {
          const dueDate = new Date(inst.due_date);
          
          // Check if installment is within date range (if filters are set)
          let inDateRange = true;
          if (startDt && dueDate < startDt) inDateRange = false;
          if (endDt && dueDate > endDt) inDateRange = false;
          
          // Also check month/year filters
          if (filterMonth && (dueDate.getMonth() + 1) !== parseInt(filterMonth)) inDateRange = false;
          if (filterYear && dueDate.getFullYear() !== parseInt(filterYear)) inDateRange = false;
          
          if (inDateRange) {
            paidInstallments.push({
              bill,
              installment: inst,
              description: bill.description,
              category_name: bill.category_name,
              category_color: bill.category_color
            });
          }
        }
      });
    });
    // Sort by due date
    return paidInstallments.sort((a, b) => 
      new Date(a.installment.due_date) - new Date(b.installment.due_date)
    );
  };

  const paidInstallments = getPaidInstallments();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getInstallmentStatus = (bill) => {
    const paid = bill.installment_details?.filter(i => i.is_paid).length || 0;
    return { paid, total: bill.installments };
  };

  // Get the installment for the filtered period (or next unpaid if no filter)
  const getFilteredInstallment = (bill) => {
    const installments = bill.installment_details || [];
    
    // Parse filter dates if set
    const startDt = filterStartDate ? new Date(filterStartDate) : null;
    const endDt = filterEndDate ? new Date(filterEndDate) : null;
    
    // If we have date or month filters, find the installment in that range
    if (startDt || endDt || filterMonth || filterYear) {
      for (const inst of installments) {
        const dueDate = new Date(inst.due_date);
        
        let inRange = true;
        if (startDt && dueDate < startDt) inRange = false;
        if (endDt && dueDate > endDt) inRange = false;
        if (filterMonth && (dueDate.getMonth() + 1) !== parseInt(filterMonth)) inRange = false;
        if (filterYear && dueDate.getFullYear() !== parseInt(filterYear)) inRange = false;
        
        if (inRange) {
          return inst;
        }
      }
    }
    
    // If no filter or no match found, return next unpaid installment
    return installments.find(i => !i.is_paid);
  };

  const getNextDueInstallment = (bill) => {
    return bill.installment_details?.find(i => !i.is_paid);
  };

  // Get month name from date
  const getMonthName = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-24 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const months = [
    { value: "1", label: "Janeiro" }, { value: "2", label: "Fevereiro" },
    { value: "3", label: "Março" }, { value: "4", label: "Abril" },
    { value: "5", label: "Maio" }, { value: "6", label: "Junho" },
    { value: "7", label: "Julho" }, { value: "8", label: "Agosto" },
    { value: "9", label: "Setembro" }, { value: "10", label: "Outubro" },
    { value: "11", label: "Novembro" }, { value: "12", label: "Dezembro" }
  ];

  const years = Array.from({ length: 5 }, (_, i) => {
    const year = new Date().getFullYear() - 2 + i;
    return { value: year.toString(), label: year.toString() };
  });

  return (
    <div className="space-y-6" data-testid="bills-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Contas a Pagar</h1>
          <p className="text-muted-foreground mt-1">{filteredBills.length} conta(s) encontrada(s)</p>
        </div>
        <div className="flex gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="export-button">
                <FileArrowDown size={18} />
                Exportar
                <CaretDown size={14} />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-40 p-2">
              <button
                onClick={() => handleExport('excel')}
                className="w-full text-left px-3 py-2 text-sm rounded hover:bg-muted"
                data-testid="export-excel"
              >
                Excel (.xlsx)
              </button>
              <button
                onClick={() => handleExport('pdf')}
                className="w-full text-left px-3 py-2 text-sm rounded hover:bg-muted"
                data-testid="export-pdf"
              >
                PDF
              </button>
            </PopoverContent>
          </Popover>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="tracing-beam gap-2" data-testid="new-bill-button">
                <Plus size={18} weight="bold" />
                Nova Conta
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editingBill ? "Editar Conta" : "Nova Conta"}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Descrição</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Ex: Conta de Luz"
                      required
                      data-testid="bill-description-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{formData.is_fixed ? "Valor Mensal" : "Valor Total"}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.total_value}
                      onChange={(e) => setFormData({ ...formData, total_value: e.target.value })}
                      placeholder="0,00"
                      required
                      data-testid="bill-value-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{formData.is_fixed ? "Meses" : "Parcelas"}</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.installments}
                      onChange={(e) => setFormData({ ...formData, installments: parseInt(e.target.value) || 1 })}
                      data-testid="bill-installments-input"
                    />
                    {formData.is_fixed && (
                      <p className="text-xs text-muted-foreground">
                        Valor de {formData.total_value ? `R$ ${parseFloat(formData.total_value).toLocaleString('pt-BR', {minimumFractionDigits: 2})}` : 'R$ 0,00'} será repetido por {formData.installments} mês(es)
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Data de Vencimento</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      data-testid="bill-date-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Categoria</Label>
                    <Select
                      value={formData.category_id || "none"}
                      onValueChange={(value) => setFormData({ ...formData, category_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger data-testid="bill-category-select">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem categoria</SelectItem>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                              {cat.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="col-span-2 space-y-2">
                    <Label>Observações</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Notas adicionais..."
                      rows={2}
                      data-testid="bill-notes-input"
                    />
                  </div>

                  <div className="col-span-2 flex items-center gap-3">
                    <Switch
                      checked={formData.is_fixed}
                      onCheckedChange={(checked) => setFormData({ 
                        ...formData, 
                        is_fixed: checked,
                        installments: checked ? 12 : 1
                      })}
                      data-testid="bill-fixed-switch"
                    />
                    <Label className="cursor-pointer">Conta Fixa (mensal)</Label>
                  </div>
                </div>

                <DialogFooter>
                  <DialogClose asChild>
                    <Button type="button" variant="outline">Cancelar</Button>
                  </DialogClose>
                  <Button type="submit" data-testid="bill-submit-button">
                    {editingBill ? "Salvar" : "Cadastrar"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Buscar</Label>
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar por descrição..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
            </div>

            <div className="w-32">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Mês</Label>
              <Select value={filterMonth || "all"} onValueChange={(val) => setFilterMonth(val === "all" ? "" : val)}>
                <SelectTrigger data-testid="filter-month">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {months.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-24">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Ano</Label>
              <Select value={filterYear || "all"} onValueChange={(val) => setFilterYear(val === "all" ? "" : val)}>
                <SelectTrigger data-testid="filter-year">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {years.map(y => (
                    <SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-36">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Categoria</Label>
              <Select value={filterCategory || "all"} onValueChange={(val) => setFilterCategory(val === "all" ? "" : val)}>
                <SelectTrigger data-testid="filter-category">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearFilters}
              className="text-muted-foreground"
              data-testid="clear-filters"
            >
              <X size={16} className="mr-1" />
              Limpar
            </Button>
          </div>

          {/* Status Filter Buttons */}
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
            <Label className="text-xs text-muted-foreground w-full mb-1">Filtrar por Status:</Label>
            <Button
              variant={statusFilter === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => { setStatusFilter("all"); setSearchParams({}); }}
              className="gap-2"
              data-testid="status-filter-all"
            >
              Todas
            </Button>
            <Button
              variant={statusFilter === "paid" ? "default" : "outline"}
              size="sm"
              onClick={() => { setStatusFilter("paid"); setSearchParams({ filter: "paid" }); }}
              className="gap-2"
              data-testid="status-filter-paid"
            >
              <CheckCircle size={16} weight={statusFilter === "paid" ? "fill" : "regular"} />
              Pagas
            </Button>
            <Button
              variant={statusFilter === "pending" ? "default" : "outline"}
              size="sm"
              onClick={() => { setStatusFilter("pending"); setSearchParams({ filter: "pending" }); }}
              className="gap-2"
              data-testid="status-filter-pending"
            >
              A Pagar
            </Button>
            <Button
              variant={statusFilter === "overdue" ? "default" : "outline"}
              size="sm"
              onClick={() => { setStatusFilter("overdue"); setSearchParams({ filter: "overdue" }); }}
              className="gap-2 text-destructive"
              data-testid="status-filter-overdue"
            >
              <Warning size={16} weight={statusFilter === "overdue" ? "fill" : "regular"} />
              Vencidas
            </Button>
          </div>

          {/* Date Range Filter - Manual Input */}
          <div className="flex flex-wrap items-end gap-3 mt-3 pt-3 border-t">
            <div className="w-36">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Data de Vencimento</Label>
              <Input
                type="date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
                className="text-sm"
                data-testid="filter-start-date"
              />
            </div>

            <div className="w-36">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Data Fim</Label>
              <Input
                type="date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
                className="text-sm"
                data-testid="filter-end-date"
              />
            </div>

            <p className="text-xs text-muted-foreground">
              Digite as datas para filtrar por período específico
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Bills List */}
      <div className="space-y-3">
        {filteredBills.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">Nenhuma conta encontrada</p>
              <Button 
                variant="link" 
                onClick={() => setDialogOpen(true)}
                className="mt-2"
              >
                Cadastrar primeira conta
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredBills.map((bill, idx) => {
            const { paid, total } = getInstallmentStatus(bill);
            const nextDue = getNextDueInstallment(bill);
            const filteredInstallment = getFilteredInstallment(bill);
            const displayInstallment = filteredInstallment || nextDue;
            const isOverdue = nextDue && new Date(nextDue.due_date) < new Date();

            return (
              <Card 
                key={bill.id} 
                className={`card-hover stagger-item ${isOverdue ? 'border-destructive/50' : ''}`}
                data-testid={`bill-card-${bill.id}`}
                data-bill-id={bill.id}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Category Color Bar */}
                    <div 
                      className="w-1.5 h-full min-h-[80px] rounded-full self-stretch"
                      style={{ backgroundColor: bill.category_color || '#8A8A8A' }}
                    />

                    {/* Main Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-heading font-semibold text-foreground">
                            {bill.description}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            {bill.category_name && (
                              <Badge 
                                variant="secondary" 
                                className="category-badge"
                                style={{ 
                                  backgroundColor: `${bill.category_color}20`,
                                  color: bill.category_color 
                                }}
                              >
                                {bill.category_name}
                              </Badge>
                            )}
                            {bill.is_fixed && (
                              <Badge variant="outline" className="text-xs">Fixo</Badge>
                            )}
                            {isOverdue && (
                              <Badge variant="destructive" className="text-xs gap-1">
                                <Warning size={12} /> Vencida
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="text-right">
                          <p className="font-mono font-bold text-lg">
                            {formatCurrency(bill.total_value)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {bill.installments}x de {formatCurrency(bill.total_value / bill.installments)}
                          </p>
                        </div>
                      </div>

                      {/* Installments Progress */}
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-muted-foreground">
                            Parcelas: {paid} de {total} pagas
                          </span>
                          {displayInstallment && (
                            <span className="text-sm text-muted-foreground">
                              Vencimento: {format(parseISO(displayInstallment.due_date), "dd/MM/yyyy")}
                              {displayInstallment.is_paid && " (Pago)"}
                            </span>
                          )}
                        </div>
                        <div className="installment-dots flex-wrap">
                          {bill.installment_details?.map((inst, i) => (
                            <button
                              key={i}
                              onClick={() => !inst.is_paid && confirmPayInstallment(bill, inst)}
                              className={`installment-dot ${inst.is_paid ? 'paid' : 'pending'} hover:scale-125 transition-transform`}
                              title={`Parcela ${inst.installment_number}: ${formatCurrency(inst.value)} - ${inst.is_paid ? 'Paga' : inst.due_date}`}
                              disabled={inst.is_paid}
                              data-testid={`installment-${bill.id}-${inst.installment_number}`}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(bill)}
                          className="gap-1.5"
                          data-testid={`edit-bill-${bill.id}`}
                        >
                          <PencilSimple size={16} />
                          Editar
                        </Button>
                        {bill.installments > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openInstallmentsModal(bill)}
                            className="gap-1.5"
                            data-testid={`manage-installments-${bill.id}`}
                          >
                            <CalendarIcon size={16} />
                            Parcelas
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => confirmDelete(bill)}
                          className="gap-1.5 text-destructive hover:text-destructive"
                          data-testid={`delete-bill-${bill.id}`}
                        >
                          <Trash size={16} />
                          Excluir
                        </Button>
                        {/* Reverter última parcela paga */}
                        {bill.installment_details?.some(i => i.is_paid) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const lastPaid = [...bill.installment_details]
                                .filter(i => i.is_paid)
                                .sort((a, b) => b.installment_number - a.installment_number)[0];
                              if (lastPaid) confirmUnpayInstallment(bill, lastPaid);
                            }}
                            className="gap-1.5 text-amber-600 hover:text-amber-700"
                            data-testid={`revert-bill-${bill.id}`}
                          >
                            <ArrowCounterClockwise size={16} />
                            Reverter
                          </Button>
                        )}
                        {nextDue && !nextDue.is_paid && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => confirmPayInstallment(bill, nextDue)}
                            className="gap-1.5 ml-auto"
                            data-testid={`pay-bill-${bill.id}`}
                          >
                            <Check size={16} />
                            Pagar Parcela {nextDue.installment_number}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Totals Summary */}
      {filteredBills.length > 0 && (
        <Card className="mt-6 bg-muted/30" data-testid="totals-summary">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                    <CurrencyDollar size={20} className="text-destructive" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      Total a Pagar
                      <button 
                        onClick={() => setShowTotalPending(!showTotalPending)}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="toggle-total-pending"
                      >
                        {showTotalPending ? <Eye size={12} /> : <EyeSlash size={12} />}
                      </button>
                    </p>
                    <p className="font-mono font-bold text-lg text-destructive">
                      {showTotalPending ? formatCurrency(totalPending) : "R$ •••••"}
                    </p>
                  </div>
                </div>
                
                {/* Clickable Total Paid */}
                <button 
                  onClick={() => setPaidModalOpen(true)}
                  className="flex items-center gap-2 p-2 -m-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group"
                  data-testid="view-paid-button"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <CheckCircle size={20} className="text-primary" />
                  </div>
                  <div className="text-left">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      Total Pago
                      <Eye size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </p>
                    <p className="font-mono font-bold text-lg text-primary">
                      {formatCurrency(totalPaid)}
                    </p>
                  </div>
                </button>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">
                  {filteredBills.length} conta(s) • Total Geral: {formatCurrency(totalPending + totalPaid)}
                </p>
                {paidInstallments.length > 0 && (
                  <p className="text-xs text-primary mt-1">
                    Clique em "Total Pago" para ver e reverter parcelas
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Paid Installments Modal */}
      <Dialog open={paidModalOpen} onOpenChange={setPaidModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CheckCircle size={24} className="text-primary" />
              Parcelas Pagas ({paidInstallments.length})
            </DialogTitle>
            <DialogDescription>
              Clique em "Reverter" para desfazer o pagamento e retornar a parcela ao mês de referência.
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh]">
            {paidInstallments.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                Nenhuma parcela paga encontrada
              </div>
            ) : (
              <div className="space-y-2 pr-4">
                {paidInstallments.map((item, idx) => (
                  <div 
                    key={`${item.bill.id}-${item.installment.installment_number}`}
                    className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                  >
                    <div 
                      className="w-1.5 h-12 rounded-full"
                      style={{ backgroundColor: item.category_color || '#8A8A8A' }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{item.description}</p>
                      <p className="text-xs text-muted-foreground">
                        Parcela {item.installment.installment_number}/{item.bill.installments}
                        {item.category_name && ` • ${item.category_name}`}
                      </p>
                      <p className="text-xs text-primary font-medium mt-0.5">
                        Ref: {getMonthName(item.installment.due_date)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-medium text-sm">
                        {formatCurrency(item.installment.value)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {item.installment.due_date}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => confirmUnpayInstallment(item.bill, item.installment)}
                      className="gap-1 text-destructive hover:text-destructive hover:bg-destructive/10"
                      data-testid={`unpay-${item.bill.id}-${item.installment.installment_number}`}
                    >
                      <ArrowCounterClockwise size={14} />
                      Reverter
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPaidModalOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Confirmation Dialog */}
      <AlertDialog open={payAlertOpen} onOpenChange={setPayAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Check size={20} className="text-primary" />
              Confirmar Pagamento
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {pendingPayment && (
                  <>
                    <p>
                      Deseja marcar como paga a <strong>parcela {pendingPayment.installment.installment_number} de {pendingPayment.bill.installments}</strong> da conta <strong>"{pendingPayment.bill.description}"</strong>?
                    </p>
                    <p className="mt-2">
                      Valor: <strong>{formatCurrency(pendingPayment.installment.value)}</strong>
                    </p>
                    <p>
                      Vencimento: <strong>{pendingPayment.installment.due_date}</strong>
                    </p>
                    
                    {/* Payment Method Selection */}
                    <div className="mt-4 pt-4 border-t">
                      <Label className="text-sm font-medium">Data do Pagamento</Label>
                      <Input
                        type="date"
                        value={selectedPayDate}
                        onChange={(e) => setSelectedPayDate(e.target.value)}
                        className="mt-2"
                        data-testid="pay-date-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Use data retroativa se o pagamento foi feito em outro mês
                      </p>
                    </div>

                    <div className="mt-4 pt-4 border-t">
                      <Label className="text-sm font-medium">Forma de Pagamento (opcional)</Label>
                      <Select
                        value={selectedPaymentMethod || "none"}
                        onValueChange={(value) => setSelectedPaymentMethod(value === "none" ? "" : value)}
                      >
                        <SelectTrigger className="mt-2" data-testid="pay-method-select">
                          <SelectValue placeholder="Selecione a forma de pagamento" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Não informar</SelectItem>
                          {paymentMethods.map(pm => (
                            <SelectItem key={pm.id} value={pm.id}>
                              {pm.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {paymentMethods.length === 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Cadastre formas de pagamento no menu "Formas de Pagamento"
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handlePayInstallment} data-testid="confirm-pay-button">
              Confirmar Pagamento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteAlertOpen} onOpenChange={setDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash size={20} />
              Confirmar Exclusão
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete && (
                <>
                  Deseja mover para a lixeira a conta <strong>"{pendingDelete.description}"</strong>?
                  <br /><br />
                  Valor Total: <strong>{formatCurrency(pendingDelete.total_value)}</strong>
                  <br />
                  Parcelas: <strong>{pendingDelete.installments}x</strong>
                  <br /><br />
                  <span className="text-muted-foreground text-xs">
                    A conta ficará na lixeira por 24 horas e poderá ser restaurada.
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-button"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unpay (Revert) Confirmation Dialog */}
      <AlertDialog open={unpayAlertOpen} onOpenChange={setUnpayAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ArrowCounterClockwise size={20} className="text-primary" />
              Reverter Pagamento
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingUnpay && (
                <>
                  Deseja reverter a <strong>parcela {pendingUnpay.installment.installment_number} de {pendingUnpay.bill.installments}</strong> da conta <strong>"{pendingUnpay.bill.description}"</strong>?
                  <br /><br />
                  Valor: <strong>{formatCurrency(pendingUnpay.installment.value)}</strong>
                  <br />
                  Mês de Referência: <strong>{getMonthName(pendingUnpay.installment.due_date)}</strong>
                  <br /><br />
                  <span className="text-muted-foreground text-xs">
                    A parcela voltará a aparecer como pendente no mês de {getMonthName(pendingUnpay.installment.due_date)}.
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleUnpayInstallment}
              data-testid="confirm-unpay-button"
            >
              Confirmar Reversão
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Installments Modal */}
      <Dialog open={installmentsModalOpen} onOpenChange={setInstallmentsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <CalendarIcon size={20} className="text-primary" />
              Gerenciar Parcelas
            </DialogTitle>
            <DialogDescription>
              {selectedBillForInstallments && (
                <>
                  <strong>{selectedBillForInstallments.description}</strong> - {selectedBillForInstallments.installments}x de {formatCurrency(selectedBillForInstallments.total_value / selectedBillForInstallments.installments)}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedBillForInstallments && (
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {selectedBillForInstallments.installment_details?.map((inst) => (
                <div 
                  key={inst.installment_number}
                  className={`flex items-center gap-4 p-3 rounded-lg border ${inst.is_paid ? 'bg-primary/5 border-primary/20' : 'bg-muted/30'}`}
                >
                  <div className="flex-shrink-0">
                    <Badge variant={inst.is_paid ? "default" : "outline"} className="w-16 justify-center">
                      {inst.installment_number}/{selectedBillForInstallments.installments}
                    </Badge>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Input
                        type="date"
                        value={editingInstallmentDates[inst.installment_number] || inst.due_date}
                        onChange={(e) => setEditingInstallmentDates(prev => ({
                          ...prev,
                          [inst.installment_number]: e.target.value
                        }))}
                        className="w-[160px]"
                        data-testid={`installment-date-${inst.installment_number}`}
                      />
                      {editingInstallmentDates[inst.installment_number] && 
                       editingInstallmentDates[inst.installment_number] !== inst.due_date && (
                        <Button
                          size="sm"
                          onClick={() => handleUpdateInstallmentDate(
                            selectedBillForInstallments.id,
                            inst.installment_number,
                            editingInstallmentDates[inst.installment_number]
                          )}
                          data-testid={`save-date-${inst.installment_number}`}
                        >
                          Salvar
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-right flex-shrink-0">
                    <p className="font-mono font-medium">
                      {formatCurrency(inst.value)}
                    </p>
                    {inst.is_paid && (
                      <span className="text-xs text-primary">Pago</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Fechar</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
